import React, { useEffect, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import Masthead, { MastheadLink, MastheadTitle } from '@amzn/meridian/masthead';
import Select, { SelectOption, SelectOptionGroup } from '@amzn/meridian/select';
import Theme from '@amzn/meridian/theme';
import { getDefaultSite } from 'src/components/shared/header/getDefaultSite';
import ProfileMenu from 'src/components/shared/header/profileMenu/profileMenu';
import * as LDAP from 'src/config/LDAP';
import logo from 'src/images/logo.png';
import { getAllowedAndPreferredSites, NO_SITE, setSelectedSite, type Site, type SiteLabel } from 'src/store/siteSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { setPreferredSite } from 'src/store/userSlice';
import { removeSpecialCharactersFromString } from 'src/utils/helpers';
import { createSearchRegExp } from 'src/utils/searchUtils';

const MASTHEAD_LINK =
  'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/b824b431-f864-4d7b-b86' +
  'd-432e392dfbf0/sheets/b824b431-f864-4d7b-b86d-432e392dfbf0_b1b816e0-74ae-4fe0-908a-fab35f0d3972';

/**
 * Main Component
 */
const Header = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { userLoading, alias, ldap, siteLocation, preferredSite, hasPermission } = useAppSelector(
    (state) => state.user,
  );
  const {
    userSites,
    allowedSites,
    isSiteSelectionDisabled,
    selectedSite,
    preferredSitesAndAllowedSitesFetched,
    hasAllowedSite,
  } = useAppSelector((state) => state.sites);

  const [searchQuery, setSearchQuery] = useState();
  const searchRegExp = createSearchRegExp(searchQuery);
  const isAdmin = ldap.includes(LDAP.ADMIN);
  const isSMES = ldap.includes(LDAP.SMES);
  const isFCLeaders = ldap.includes(LDAP.FC_LEADERS);
  const isDev = ldap.includes(LDAP.DEV);

  const filteredPreferredSites =
    userSites?.length >= 1
      ? userSites
          .map((site) => ({ label: site.site, value: site.id }))
          .filter((option) => !searchQuery || searchRegExp.test(option.label))
          .sort((a, b) => a.label.localeCompare(b.label))
      : [];
  const filteredAllowedSites =
    Array.isArray(allowedSites) && allowedSites.length >= 1
      ? allowedSites.filter((option) => !searchQuery || searchRegExp.test(option.site))
      : [];

  const filterNotPreferredSitesFromAllowedSites = (filteredPreferredSites: SiteLabel[], allowedSites: Site[]) => {
    const preferredSiteIdsSet = new Set(filteredPreferredSites.map((site: { value: number }) => site.value));
    return allowedSites.filter((site) => !preferredSiteIdsSet.has(site.id));
  };

  const filteredNotPreferredSites = filterNotPreferredSitesFromAllowedSites(
    filteredPreferredSites,
    filteredAllowedSites,
  )
    .map((site) => ({ label: site.site, value: site.id }))
    .sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    // get initial data
    if (userLoading === 'fulfilled' && alias !== '') {
      dispatch(getAllowedAndPreferredSites(alias));
    }
  }, [alias]);

  useEffect(() => {
    if (selectedSite === NO_SITE && allowedSites.length > 0) {
      const siteParam = getSiteParamFromPath(location.pathname);
      const defaultSite = getDefaultSite(filteredPreferredSites, allowedSites, siteLocation, siteParam);
      dispatch(setPreferredSite(defaultSite.id));
      dispatch(setSelectedSite(defaultSite));
    }
  }, [preferredSitesAndAllowedSitesFetched]);

  const updateSelectedSite = (siteId: number) => {
    const selectedSite = allowedSites.filter((site) => site.id === siteId)[0];
    dispatch(setPreferredSite(siteId));
    dispatch(setSelectedSite(selectedSite));
  };

  const getSiteParamFromPath = (pathname: string) => {
    // Example pathname with site param will look like:
    // "/placementsOverview/ABE3/pendingPlacements"
    // which when split by "/" will result in [,"placementsOverview","ABE3", "pendingPlacements"]
    return pathname.includes('/placementsOverview') ? pathname.split('/')[2] : undefined;
  };

  return (
    <Theme palette="blue" mode="dark">
      <Masthead backgroundColor="highlight">
        {/* Links */}
        <MastheadTitle>
          <img src={logo} onClick={() => navigate('/')} width="408" height="52" />
        </MastheadTitle>
        <MastheadLink
          onClick={() => navigate('/placementsOverview')}
          selected={location.pathname === '/placementsOverview'}
          data-cy="placements_overview__link"
        >
          Placements Overview
        </MastheadLink>
        {(isDev || isAdmin || isSMES || isFCLeaders) && (
          <MastheadLink
            onClick={() => navigate('/siteManagement')}
            selected={location.pathname.includes('/siteManagement')}
            data-cy="site_management__link"
          >
            Site Management
          </MastheadLink>
        )}
        {hasPermission && hasAllowedSite && (
          <MastheadLink
            onClick={() => window.open(MASTHEAD_LINK, '_blank')}
            selected={location.pathname === '/reporting'}
          >
            Reporting
          </MastheadLink>
        )}

        {/* The page that will be affected by site selection here (landing page) has not been added yet */}
        <Select
          value={preferredSite}
          onChange={updateSelectedSite}
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Selected Site..."
          width={200}
          disabled={isSiteSelectionDisabled}
          data-cy="site__select"
        >
          <SelectOptionGroup label="Preferred Sites">
            {filteredPreferredSites.map((option) => (
              <SelectOption
                label={option.label}
                value={option.value}
                key={option.value}
                data-cy={`site-select__preferredsite-${removeSpecialCharactersFromString(option.label)}`}
              />
            ))}
          </SelectOptionGroup>
          <SelectOptionGroup label="All Sites" data-cy="site-select__allsite-selectOptionGroup">
            {filteredNotPreferredSites.map((option) => (
              <SelectOption
                label={option.label}
                value={option.value}
                key={option.value}
                data-cy={`site-select__allsite-${removeSpecialCharactersFromString(option.label)}`}
              />
            ))}
          </SelectOptionGroup>
        </Select>
        <ProfileMenu></ProfileMenu>
      </Masthead>
    </Theme>
  );
};

export default Header;
